import { useMutation, useQuery } from '@tanstack/react-query';
import {
  executeDetails,
  executeFilter,
  getAvailableFilterTypes,
  getFilterPropertyDefinitions,
} from '../api/kirbyFiltering';
import { KirbyDetailsIdentifier, KirbyFilterRequest } from '../types/kirbyFiltering';

const filteringQueryKey = 'KirbyFiltering';

/** Loads and caches the available Kirby filter categories. */
export const useAvailableFilterTypes = () =>
  useQuery({
    queryKey: [filteringQueryKey, 'filterTypes'],
    queryFn: getAvailableFilterTypes,
    refetchOnWindowFocus: false,
  });

/** Loads property metadata only after a filter category is selected. */
export const useFilterPropertyDefinitions = (filterType: string) =>
  useQuery({
    queryKey: [filteringQueryKey, 'propertyDefinitions', filterType],
    queryFn: () => getFilterPropertyDefinitions(filterType),
    enabled: Boolean(filterType),
    refetchOnWindowFocus: false,
  });

interface ExecuteFilterVariables {
  filterType: string;
  request: KirbyFilterRequest;
}

/** Executes only explicitly submitted Kirby filter drafts. */
export const useExecuteFilter = () =>
  useMutation({
    mutationFn: ({ filterType, request }: ExecuteFilterVariables) => executeFilter(filterType, request),
  });

interface ExecuteDetailsVariables {
  filterType: string;
  id: KirbyDetailsIdentifier;
  requestReason: string;
}

/** Retrieves a selected Kirby record's details after an audit reason is provided. */
export const useExecuteDetails = () =>
  useMutation({
    mutationFn: ({ filterType, id, requestReason }: ExecuteDetailsVariables) =>
      executeDetails(filterType, id, requestReason),
  });
